import { useEffect, useMemo, useState } from 'react';
import type { BreadcrumbTrailItem } from '../ui/breadcrumb';

const NAVIGATION_HISTORY_KEY = 'eliza.navigationHistory.v1';
const MAX_HISTORY_ENTRIES = 30;
const RESET_AREA_IDS = new Set(['home']);

interface RouteLocationLike {
  pathname: string;
  search?: string;
  hash?: string;
}

export interface NavigationHistoryEntry {
  href: string;
  pathname: string;
  search: string;
  hash: string;
  areaId: string;
  label: string;
  timestamp: number;
}

export interface NavigationReturnTarget {
  href: string;
  label: string;
}

const AREA_LABELS: Record<string, string> = {
  'admin-settings': 'Admin Settings',
  'ai-console': 'AI Console',
  'platform-settings': 'Platform Settings',
  catalog: 'Catalog',
  workspaces: 'Workspaces',
};

function normalizePath(pathname: string): string {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

function getFirstSegment(pathname: string): string {
  return normalizePath(pathname).split('/').filter(Boolean)[0] || 'home';
}

export function getNavigationAreaId(pathname: string): string {
  const normalizedPath = normalizePath(pathname);
  const firstSegment = getFirstSegment(normalizedPath);

  if (
    normalizedPath.startsWith('/admin/catalog')
    || normalizedPath === '/admin/ontology'
    || normalizedPath === '/admin/context-packs'
  ) {
    return 'catalog';
  }

  if (
    firstSegment === 'data-connections'
    || firstSegment === 'ttc-data-products'
    || firstSegment === 'tenant-admin'
    || (
      firstSegment === 'admin'
      && !normalizedPath.startsWith('/admin/agents')
    )
  ) {
    return 'admin-settings';
  }

  if (
    firstSegment === 'ai-console'
    || firstSegment === 'telemetry'
    || firstSegment === 'evals'
    || firstSegment === 'gepa-optimizer'
    || firstSegment === 'prompt-management'
  ) {
    return 'ai-console';
  }

  if (firstSegment === 'platform-admin') {
    return 'platform-settings';
  }

  return firstSegment;
}

function isResetEntry(entry: NavigationHistoryEntry): boolean {
  return RESET_AREA_IDS.has(entry.areaId);
}

function labelToString(label: BreadcrumbTrailItem['label']): string | undefined {
  if (typeof label === 'string') return label;
  if (typeof label === 'number') return String(label);
  return undefined;
}

function getWorkspaceLabel(pathname: string, breadcrumbs: BreadcrumbTrailItem[]): string | undefined {
  const normalizedPath = normalizePath(pathname);
  if (!normalizedPath.startsWith('/workspaces/')) return undefined;
  const workspaceCrumb = breadcrumbs.find((item, index) => index > 0 && labelToString(item.label));
  return workspaceCrumb ? labelToString(workspaceCrumb.label) : undefined;
}

function getEntryLabel(
  pathname: string,
  areaId: string,
  breadcrumbs: BreadcrumbTrailItem[],
): string {
  const workspaceLabel = areaId === 'workspaces'
    ? getWorkspaceLabel(pathname, breadcrumbs)
    : undefined;
  if (workspaceLabel) return workspaceLabel;

  if (AREA_LABELS[areaId]) return AREA_LABELS[areaId];

  const firstLabel = breadcrumbs.map(item => labelToString(item.label)).find(Boolean);
  if (firstLabel) return firstLabel;

  return getFirstSegment(pathname)
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

export function createNavigationEntry(
  location: RouteLocationLike,
  breadcrumbs: BreadcrumbTrailItem[],
  timestamp = Date.now(),
): NavigationHistoryEntry {
  const pathname = normalizePath(location.pathname);
  const search = location.search ?? '';
  const hash = location.hash ?? '';
  const areaId = getNavigationAreaId(pathname);

  return {
    href: `${pathname}${search}${hash}`,
    pathname,
    search,
    hash,
    areaId,
    label: getEntryLabel(pathname, areaId, breadcrumbs),
    timestamp,
  };
}

function readHistory(): NavigationHistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(NAVIGATION_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeHistory(entries: NavigationHistoryEntry[]) {
  try {
    sessionStorage.setItem(NAVIGATION_HISTORY_KEY, JSON.stringify(entries));
  } catch {
    // sessionStorage can be unavailable in restricted browser contexts.
  }
}

export function updateNavigationHistory(
  entries: NavigationHistoryEntry[],
  currentEntry: NavigationHistoryEntry,
): NavigationHistoryEntry[] {
  if (isResetEntry(currentEntry)) {
    return [];
  }

  const previousEntry = entries[entries.length - 1];
  if (previousEntry?.href === currentEntry.href) {
    return [
      ...entries.slice(0, -1),
      { ...previousEntry, ...currentEntry },
    ];
  }

  const entryBeforePrevious = entries[entries.length - 2];
  if (entryBeforePrevious?.href === currentEntry.href) {
    return [
      ...entries.slice(0, -2),
      { ...entryBeforePrevious, ...currentEntry },
    ];
  }

  return [...entries, currentEntry].slice(-MAX_HISTORY_ENTRIES);
}

export function getNavigationReturnTarget(
  entries: NavigationHistoryEntry[],
  currentEntry: NavigationHistoryEntry,
): NavigationReturnTarget | null {
  for (let index = entries.length - 2; index >= 0; index -= 1) {
    const entry = entries[index];
    if (
      entry.href !== currentEntry.href
      && entry.areaId !== currentEntry.areaId
      && !isResetEntry(entry)
    ) {
      return {
        href: entry.href,
        label: entry.label,
      };
    }
  }
  return null;
}

export function useNavigationReturnTarget(
  location: RouteLocationLike,
  breadcrumbs: BreadcrumbTrailItem[],
): NavigationReturnTarget | null {
  const currentEntry = useMemo(
    () => createNavigationEntry(location, breadcrumbs),
    [location, breadcrumbs],
  );
  const [returnTarget, setReturnTarget] = useState<NavigationReturnTarget | null>(null);

  useEffect(() => {
    const nextHistory = updateNavigationHistory(readHistory(), currentEntry);
    writeHistory(nextHistory);
    setReturnTarget(getNavigationReturnTarget(nextHistory, currentEntry));
  }, [currentEntry]);

  return returnTarget;
}
